"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { Button } from '@open-mercato/ui/primitives/button'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCallOrThrow, readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'

type InvoiceLineItem = {
  id: string
  lineNumber: number
  description: string
  quantity: number
  unit: string
  unitPriceNet: number
  netAmount: number
  vatRate: number
  vatAmount: number
  grossAmount: number
  currencyCode: string
}

type InvoiceDetail = {
  id: string
  invoiceNumber: string
  status: string
  transportMode: string
  dispatchId: string | null
  legId: string | null
  cargoId: string | null
  issuerName: string
  issuerNip: string | null
  recipientName: string
  recipientNip: string | null
  issueDate: string | null
  serviceDate: string | null
  dueDate: string
  currencyCode: string
  vatRate: number
  netAmount: number
  vatAmount: number
  grossAmount: number
  paymentTermsDays: number
  paymentReference: string | null
  notes: string | null
  ksefReference: string | null
  ksefSubmittedAt: string | null
  createdAt: string
  updatedAt: string
  lineItems: InvoiceLineItem[]
}

const statusBadgeClass: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  ISSUED: 'bg-blue-100 text-blue-800',
  SENT: 'bg-indigo-100 text-indigo-800',
  PAID: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
  CREDITED: 'bg-yellow-100 text-yellow-800',
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function normalizeLineItem(raw: Record<string, unknown>): InvoiceLineItem {
  return {
    id: String(raw.id ?? ''),
    lineNumber: toNumber(raw.lineNumber),
    description: typeof raw.description === 'string' ? raw.description : '',
    quantity: toNumber(raw.quantity),
    unit: typeof raw.unit === 'string' ? raw.unit : '',
    unitPriceNet: toNumber(raw.unitPriceNet),
    netAmount: toNumber(raw.netAmount),
    vatRate: toNumber(raw.vatRate),
    vatAmount: toNumber(raw.vatAmount),
    grossAmount: toNumber(raw.grossAmount),
    currencyCode: typeof raw.currencyCode === 'string' ? raw.currencyCode : 'PLN',
  }
}

function normalizeInvoice(raw: Record<string, unknown>): InvoiceDetail {
  const lineItemsRaw = Array.isArray(raw.lineItems) ? (raw.lineItems as Record<string, unknown>[]) : []
  return {
    id: String(raw.id ?? ''),
    invoiceNumber: typeof raw.invoiceNumber === 'string' ? raw.invoiceNumber : '',
    status: typeof raw.status === 'string' ? raw.status : 'DRAFT',
    transportMode: typeof raw.transportMode === 'string' ? raw.transportMode : 'ROAD',
    dispatchId: typeof raw.dispatchId === 'string' ? raw.dispatchId : null,
    legId: typeof raw.legId === 'string' ? raw.legId : null,
    cargoId: typeof raw.cargoId === 'string' ? raw.cargoId : null,
    issuerName: typeof raw.issuerName === 'string' ? raw.issuerName : '',
    issuerNip: typeof raw.issuerNip === 'string' ? raw.issuerNip : null,
    recipientName: typeof raw.recipientName === 'string' ? raw.recipientName : '',
    recipientNip: typeof raw.recipientNip === 'string' ? raw.recipientNip : null,
    issueDate: typeof raw.issueDate === 'string' ? raw.issueDate : null,
    serviceDate: typeof raw.serviceDate === 'string' ? raw.serviceDate : null,
    dueDate: typeof raw.dueDate === 'string' ? raw.dueDate : '',
    currencyCode: typeof raw.currencyCode === 'string' ? raw.currencyCode : 'PLN',
    vatRate: toNumber(raw.vatRate),
    netAmount: toNumber(raw.netAmount),
    vatAmount: toNumber(raw.vatAmount),
    grossAmount: toNumber(raw.grossAmount),
    paymentTermsDays: toNumber(raw.paymentTermsDays),
    paymentReference: typeof raw.paymentReference === 'string' ? raw.paymentReference : null,
    notes: typeof raw.notes === 'string' ? raw.notes : null,
    ksefReference: typeof raw.ksefReference === 'string' ? raw.ksefReference : null,
    ksefSubmittedAt: typeof raw.ksefSubmittedAt === 'string' ? raw.ksefSubmittedAt : null,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : '',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : '',
    lineItems: lineItemsRaw.map(normalizeLineItem),
  }
}

function formatDate(value: string | null): string {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleDateString()
}

function formatMoney(amount: number, currency: string): string {
  return `${amount.toFixed(2)} ${currency}`
}

export default function FreightInvoiceDetailPage({ params }: { params?: { id?: string } }) {
  const invoiceId = params?.id ?? null
  const t = useT()
  const router = useRouter()
  const { confirm } = useConfirmDialog()

  const [invoice, setInvoice] = React.useState<InvoiceDetail | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const loadDetail = React.useCallback(async () => {
    if (!invoiceId) {
      setError(t('billing.detail.errors.missingId', 'Invoice id is missing.'))
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const raw = await readApiResultOrThrow<Record<string, unknown>>(
        `/api/freight/billing/invoices/${encodeURIComponent(invoiceId)}`,
        undefined,
        { errorMessage: t('billing.detail.loadError', 'Failed to load invoice.') },
      )
      setInvoice(normalizeInvoice(raw))
    } catch (loadError) {
      setError(
        loadError instanceof Error && loadError.message
          ? loadError.message
          : t('billing.detail.loadError', 'Failed to load invoice.'),
      )
      setInvoice(null)
    } finally {
      setIsLoading(false)
    }
  }, [invoiceId, t])

  React.useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  const handleFinalize = React.useCallback(async () => {
    if (!invoice) return
    const confirmed = await confirm({
      title: t('billing.actions.finalizeConfirm', 'Finalize invoice?'),
      text: t(
        'billing.actions.finalizeDescription',
        'The invoice number will be assigned and the record will become immutable.',
      ),
    })
    if (!confirmed) return
    try {
      await apiCallOrThrow(`/api/freight/billing/invoices/${encodeURIComponent(invoice.id)}/finalize`, {
        method: 'POST',
      })
      flash(t('billing.actions.finalized', 'Invoice finalized.'), 'success')
      await loadDetail()
    } catch (finalizeError) {
      flash(
        finalizeError instanceof Error && finalizeError.message
          ? finalizeError.message
          : t('billing.errors.finalizeFailed', 'Failed to finalize invoice.'),
        'error',
      )
    }
  }, [invoice, confirm, loadDetail, t])

  const handleDelete = React.useCallback(async () => {
    if (!invoice) return
    const confirmed = await confirm({
      title: t('billing.actions.deleteConfirm', 'Delete invoice?'),
      text: t('billing.actions.deleteDescription', 'Drafts can be deleted. Issued invoices cannot be removed.'),
    })
    if (!confirmed) return
    try {
      await apiCallOrThrow(`/api/freight/billing/invoices/${encodeURIComponent(invoice.id)}`, {
        method: 'DELETE',
      })
      flash(t('billing.actions.deleted', 'Invoice deleted.'), 'success')
      router.push('/backend/billing/invoices')
    } catch (deleteError) {
      flash(
        deleteError instanceof Error && deleteError.message
          ? deleteError.message
          : t('billing.errors.deleteFailed', 'Failed to delete invoice.'),
        'error',
      )
    }
  }, [invoice, confirm, router, t])

  if (isLoading) {
    return (
      <Page>
        <PageBody>
          <LoadingMessage label={t('billing.detail.loading', 'Loading invoice…')} />
        </PageBody>
      </Page>
    )
  }

  if (error || !invoice) {
    return (
      <Page>
        <PageBody>
          <ErrorMessage label={error ?? t('billing.detail.notFound', 'Invoice was not found.')} />
        </PageBody>
      </Page>
    )
  }

  const isDraft = invoice.status === 'DRAFT'

  return (
    <Page>
      <PageBody>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('billing.detail.label', 'Freight invoice')}
              </div>
              <h1 className="text-2xl font-bold">
                {invoice.invoiceNumber || t('billing.fields.draft', '(draft)')}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass[invoice.status] ?? 'bg-gray-100 text-gray-800'}`}>
                  {t(`billing.status.${invoice.status}`, invoice.status)}
                </span>
                <span>{t(`billing.transport_mode.${invoice.transportMode}`, invoice.transportMode)}</span>
                {invoice.dispatchId ? (
                  <Link
                    href={`/backend/dispatch?id=${encodeURIComponent(invoice.dispatchId)}`}
                    className="text-primary hover:underline"
                  >
                    {t('billing.detail.openDispatch', 'Open dispatch')}
                  </Link>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild type="button" variant="outline">
                <Link href="/backend/billing/invoices">
                  {t('billing.actions.back', 'Back to list')}
                </Link>
              </Button>
              {isDraft ? (
                <Button type="button" onClick={handleFinalize}>
                  {t('billing.actions.finalize', 'Finalize')}
                </Button>
              ) : null}
              {isDraft ? (
                <Button type="button" variant="destructive" onClick={handleDelete}>
                  {t('common.delete', 'Delete')}
                </Button>
              ) : null}
            </div>
          </div>

          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('billing.detail.sections.parties', 'Parties')}</div>
              <dl className="mt-3 space-y-2 text-sm">
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.issuerName', 'Issuer')}
                  </dt>
                  <dd>{invoice.issuerName}{invoice.issuerNip ? ` — NIP ${invoice.issuerNip}` : ''}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.recipientName', 'Recipient')}
                  </dt>
                  <dd>{invoice.recipientName}{invoice.recipientNip ? ` — NIP ${invoice.recipientNip}` : ''}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('billing.detail.sections.dates', 'Dates and payment')}</div>
              <dl className="mt-3 space-y-2 text-sm">
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.issueDate', 'Issued')}
                  </dt>
                  <dd>{formatDate(invoice.issueDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.serviceDate', 'Service date')}
                  </dt>
                  <dd>{formatDate(invoice.serviceDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.dueDate', 'Due date')}
                  </dt>
                  <dd>{formatDate(invoice.dueDate)}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.paymentTermsDays', 'Payment terms (days)')}
                  </dt>
                  <dd>{invoice.paymentTermsDays}</dd>
                </div>
                {invoice.paymentReference ? (
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">
                      {t('billing.fields.paymentReference', 'Payment reference')}
                    </dt>
                    <dd>{invoice.paymentReference}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">{t('billing.detail.sections.lineItems', 'Line items')}</div>
              <div className="text-xs text-muted-foreground">
                {t('billing.detail.lineItemsCount', '{{count}} items', { count: invoice.lineItems.length })}
              </div>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-4">{t('billing.fields.lineNumber', '#')}</th>
                    <th className="py-2 pr-4">{t('billing.fields.description', 'Description')}</th>
                    <th className="py-2 pr-4 text-right">{t('billing.fields.quantity', 'Qty')}</th>
                    <th className="py-2 pr-4 text-right">{t('billing.fields.unitPriceNet', 'Unit net')}</th>
                    <th className="py-2 pr-4 text-right">{t('billing.fields.netAmount', 'Net')}</th>
                    <th className="py-2 pr-4 text-right">{t('billing.fields.vatRate', 'VAT %')}</th>
                    <th className="py-2 pr-4 text-right">{t('billing.fields.vatAmount', 'VAT')}</th>
                    <th className="py-2 text-right">{t('billing.fields.grossAmount', 'Gross')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((line) => (
                    <tr key={line.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4">{line.lineNumber}</td>
                      <td className="py-2 pr-4">{line.description}</td>
                      <td className="py-2 pr-4 text-right">{line.quantity} {line.unit}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(line.unitPriceNet, line.currencyCode)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(line.netAmount, line.currencyCode)}</td>
                      <td className="py-2 pr-4 text-right">{line.vatRate.toFixed(0)}%</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(line.vatAmount, line.currencyCode)}</td>
                      <td className="py-2 text-right">{formatMoney(line.grossAmount, line.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t text-right font-medium">
                    <td className="py-2 pr-4" colSpan={4}>{t('billing.detail.totals', 'Totals')}</td>
                    <td className="py-2 pr-4">{formatMoney(invoice.netAmount, invoice.currencyCode)}</td>
                    <td className="py-2 pr-4">-</td>
                    <td className="py-2 pr-4">{formatMoney(invoice.vatAmount, invoice.currencyCode)}</td>
                    <td className="py-2">{formatMoney(invoice.grossAmount, invoice.currencyCode)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {invoice.notes ? (
            <section className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('billing.fields.notes', 'Notes')}</div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{invoice.notes}</p>
            </section>
          ) : null}

          {invoice.ksefReference ? (
            <section className="rounded-lg border bg-card p-4">
              <div className="text-sm font-semibold">{t('billing.detail.sections.ksef', 'KSeF')}</div>
              <dl className="mt-3 space-y-2 text-sm">
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">
                    {t('billing.fields.ksefReference', 'KSeF reference')}
                  </dt>
                  <dd>{invoice.ksefReference}</dd>
                </div>
                {invoice.ksefSubmittedAt ? (
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">
                      {t('billing.fields.ksefSubmittedAt', 'Submitted at')}
                    </dt>
                    <dd>{formatDate(invoice.ksefSubmittedAt)}</dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}
        </div>
      </PageBody>
    </Page>
  )
}
